import { useQuery } from "@apollo/client";
import { Col, DatePicker, Empty, Form, Row, Table, Tag, Typography } from "antd";
import Search from "antd/lib/input/Search";
import { PaginationConfig } from "antd/lib/pagination";
import { format } from "date-fns";
import React, { useState } from "react";
import moment from "moment";
import Error from "../../components/apollo_error";
import LoadingSpinner from "../../components/loading_indicator";
import {
  GENERATE_INVENTORY_REPORT,
  getGenerateInventoryReportVariables,
  get_total_product_stock_items_count,
} from "../../graphql/inventory";
import withClinic from "../../hooks/with_clinic";
import { ApolloResult, Clinic } from "../../store";

interface P {
  currentClinic?: Clinic;
}

interface InventoryReportItem {
  id: string;
  name: string;
  current_qty: number;
  received_qty: number;
  sale_qty: number;
  adjustment_in_qty: number;
  adjustment_out_qty: number;
}

const InventoryReportPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [reportDate, setReportDate] = useState(moment().utc().startOf('day'));

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const toDate = reportDate.utc().startOf('day').toDate();
  
  const { variables } = getGenerateInventoryReportVariables({
    clinicId,
    take,
    skip,
    searchText,
    toDate: toDate,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"generateInventoryReport", InventoryReportItem[]>
  >(GENERATE_INVENTORY_REPORT, { variables });

  // Get total count for pagination
  const { variables: countVariables } = getGenerateInventoryReportVariables({
    clinicId,
    take: 1,
    skip: 0,
    searchText,
    toDate: toDate,
  });

  const totalCount = useQuery<
    ApolloResult<"aggregateProductStockItem", { _count: { id: number } }>
  >(get_total_product_stock_items_count, { variables: countVariables });

  if (loading || totalCount.loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (totalCount.error) return <Error error={totalCount.error} />;
  if (!data) return <Empty />;

  const onDateChange = (date: any) => {
    if (date) {
      setReportDate(date.utc().startOf('day'));
    }
  };



  const columns: any[] = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      editable: false,
      width: 250,
      ellipsis: true,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    },
    {
      title: "Current Stock",
      dataIndex: "current_qty",
      key: "current_qty",
      editable: false,
      width: 120,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) => a.current_qty - b.current_qty,
      render: (v: number, r: InventoryReportItem) => {
        return (
          <span style={{ fontWeight: 600 }}>
            {r.current_qty}
          </span>
        );
      },
    },

    {
      title: "Received Qty",
      dataIndex: "received_qty",
      key: "received_qty",
      width: 130,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) => a.received_qty - b.received_qty,
      editable: false,
      render: (v: number, r: InventoryReportItem) => {
        return (
          <span style={{ color: "green", fontWeight: 500 }}>
            {r.received_qty}
          </span>
        );
      },
    },
    {
      title: "Sale Qty",
      dataIndex: "sale_qty",
      key: "sale_qty",
      width: 120,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) => a.sale_qty - b.sale_qty,
      editable: false,
      render: (v: number, r: InventoryReportItem) => {
        return (
          <span style={{ color: "red", fontWeight: 500 }}>
            {r.sale_qty}
          </span>
        );
      },
    },
    {
      title: "Adjustment In",
      dataIndex: "adjustment_in_qty",
      key: "adjustment_in_qty",
      width: 140,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) => a.adjustment_in_qty - b.adjustment_in_qty,
      editable: false,
      render: (v: number, r: InventoryReportItem) => {
        return (
          <span style={{ color: "blue", fontWeight: 500 }}>
            {r.adjustment_in_qty}
          </span>
        );
      },
    },
    {
      title: "Adjustment Out",
      dataIndex: "adjustment_out_qty",
      key: "adjustment_out_qty",
      width: 140,
      sorter: (a: InventoryReportItem, b: InventoryReportItem) => a.adjustment_out_qty - b.adjustment_out_qty,
      editable: false,
      render: (v: number, r: InventoryReportItem) => {
        return (
          <span style={{ color: "orange", fontWeight: 500 }}>
            {r.adjustment_out_qty}
          </span>
        );
      },
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Inventory Report
            </Typography.Title>
            <Typography.Text type="secondary">
              as of {reportDate.format("YYYY-MM-DD")}
            </Typography.Text>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder="Search product names..."
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DatePicker
          value={reportDate}
          onChange={onDateChange}
          format="YYYY-MM-DD"
          placeholder="Select date"
          style={{ width: 150 }}
        />
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form component={false}>
          <Table
            showHeader={data.generateInventoryReport.length > 0}
            dataSource={data.generateInventoryReport.map((b) => ({ key: b.id, ...b }))}
            columns={columns}
            rowClassName="editable-row"
            onChange={(p: PaginationConfig) => {
              setPagination(p);
            }}
            pagination={{
              total: totalCount.data?.aggregateProductStockItem._count.id || 0,
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            scroll={{ x: 1000 }}
          />
        </Form>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {context}
    </React.Fragment>
  );
};

export default withClinic(InventoryReportPage);
